import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePageMeta } from '@/hooks/usePageMeta';
import JsonLd from '@/components/feature/JsonLd';
import Breadcrumb from '@/components/feature/Breadcrumb';
import StoryCard from '@/components/feature/StoryCard';
import { categories } from '@/mocks/categories';
import { stories } from '@/mocks/stories';
import StoriesSidebar from './components/StoriesSidebar';

type SortKey = 'latest' | 'hot';

export default function Stories() {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get('category') ?? 'all';
  const q = searchParams.get('q') ?? '';

  const [keyword, setKeyword] = useState(q);
  const [sort, setSort] = useState<SortKey>('latest');

  useEffect(() => {
    setKeyword(q);
  }, [q]);

  const activeCat = categories.find((c) => c.slug === category);

  const title = activeCat ? `${activeCat.name} · 高校吃瓜趣闻合集` : '全部趣闻 · 高校吃瓜趣闻与校园日常';

  usePageMeta({
    title: `${title} | 瓜田笔记`,
    description: activeCat
      ? `瓜田笔记「${activeCat.name}」分类合集，${activeCat.desc} 持续更新高校吃瓜日常与校园故事。`
      : '浏览瓜田笔记全部高校吃瓜趣闻与日常分享，涵盖宿舍夜聊、课堂八卦、社团趣事、食堂奇遇和校园奇闻，支持分类筛选与关键词搜索。',
    keywords: '高校吃瓜,高校吃瓜趣闻,高校吃瓜日常,校园故事,校园日常分享',
    path: '/stories',
  });

  const filtered = useMemo(() => {
    const kw = q.trim();
    const list = stories.filter((s) => {
      const matchCat = category === 'all' || s.categorySlug === category;
      const matchKw =
        !kw ||
        s.title.includes(kw) ||
        s.excerpt.includes(kw) ||
        s.categoryName.includes(kw) ||
        s.tags.some((t) => t.includes(kw));
      return matchCat && matchKw;
    });
    return [...list].sort((a, b) =>
      sort === 'hot' ? b.views - a.views : a.date < b.date ? 1 : -1,
    );
  }, [category, q, sort]);

  const selectCategory = (slug: string) => {
    const next = new URLSearchParams(searchParams);
    if (slug === 'all') next.delete('category');
    else next.set('category', slug);
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (keyword.trim()) next.set('q', keyword.trim());
    else next.delete('q');
    setSearchParams(next);
  };

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: title,
      inLanguage: 'zh-CN',
      description: activeCat ? activeCat.desc : '瓜田笔记全部高校吃瓜趣闻与日常合集',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: `${window.location.origin}/` },
        { '@type': 'ListItem', position: 2, name: '全部趣闻', item: `${window.location.origin}/stories` },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: filtered.map((s, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: s.title,
        url: `${window.location.origin}/stories/${s.id}`,
      })),
    },
  ];

  return (
    <>
      <JsonLd data={structuredData} />

      <section className="border-b border-background-200 bg-background-100/50">
        <div className="mx-auto w-full max-w-content px-4 py-10 md:px-6 md:py-14">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '全部趣闻', to: activeCat ? '/stories' : undefined },
              ...(activeCat ? [{ label: activeCat.name }] : []),
            ]}
          />

          <div className="mt-5 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <h1 className="font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
                {activeCat ? activeCat.name : '全部趣闻'}
              </h1>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                {activeCat
                  ? activeCat.desc
                  : '这里收录了瓜田笔记的全部高校吃瓜趣闻与日常故事。用分类筛选或关键词搜索，快速找到你想围观的校园场景。'}
              </p>
            </div>

            <form onSubmit={submitSearch} className="flex w-full max-w-sm gap-2">
              <div className="relative flex-1">
                <i className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400" />
                <input
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索标题或话题"
                  aria-label="搜索趣闻"
                  className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </div>
              <button
                type="submit"
                className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                搜索
              </button>
            </form>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="mx-auto grid w-full max-w-content grid-cols-1 gap-8 px-4 md:px-6 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="flex flex-col gap-4 border-b border-background-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => selectCategory('all')}
                  className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                    category === 'all'
                      ? 'bg-primary-500 text-background-50'
                      : 'border border-background-200 bg-background-50 text-foreground-700 hover:border-primary-300'
                  }`}
                >
                  全部
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.slug}
                    type="button"
                    onClick={() => selectCategory(cat.slug)}
                    className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                      category === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'border border-background-200 bg-background-50 text-foreground-700 hover:border-primary-300'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2 text-xs text-foreground-500">
                <span className="whitespace-nowrap">共 {filtered.length} 篇</span>
                <span className="h-4 w-px bg-background-300" />
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value as SortKey)}
                  aria-label="排序方式"
                  className="rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-xs text-foreground-700 outline-none focus:border-primary-400"
                >
                  <option value="latest">最新发布</option>
                  <option value="hot">围观最多</option>
                </select>
              </div>
            </div>

            {q && (
              <p className="mt-4 text-sm text-foreground-600">
                关于「<span className="font-medium text-primary-700">{q}</span>」的搜索结果
              </p>
            )}

            {filtered.length > 0 ? (
              <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
                {filtered.map((story) => (
                  <StoryCard key={story.id} story={story} />
                ))}
              </div>
            ) : (
              <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100/50 py-16 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                  <i className="ri-search-eye-line text-2xl" />
                </span>
                <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">暂时没找到相关的瓜</h2>
                <p className="mt-2 max-w-sm text-sm text-foreground-600">
                  换个关键词试试，或者看看全部趣闻，也许有意外收获。
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setKeyword('');
                    setSearchParams(new URLSearchParams());
                  }}
                  className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  查看全部趣闻
                </button>
              </div>
            )}
          </div>

          <div className="lg:col-span-4">
            <StoriesSidebar
              activeCategory={category}
              onSelectCategory={selectCategory}
            />
          </div>
        </div>
      </section>
    </>
  );
}